import React, { useState, useEffect, useCallback } from 'react';

const fetchPrintersFromBartender = async (authToken) => {
  if (!authToken) {
    return { success: false, error: 'No authentication token provided' };
  }

  try {
    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
    const response = await fetch(`${apiBase}/batender-requests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        method: 'GET',
        url: 'https://am1.bartendercloud.com/api/printers'
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json()
    return { success: true, data: data };
  } catch (error) {
    console.error('Error fetching printers:', error);
    return { success: false, error: 'Failed to retrieve printers from Bartender Cloud' };
  }
};

const PrinterSelect = ({ selectedPrinter, onPrinterChange, authToken }) => {
  const [printers, setPrinters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [hasRestoredFromStorage, setHasRestoredFromStorage] = useState(false);

  // Save selected printer to localStorage whenever it changes
  useEffect(() => {
    if (selectedPrinter) {
      localStorage.setItem('mixo-printer-last-selected', selectedPrinter);
    }
  }, [selectedPrinter]);

  const loadPrinters = useCallback(async () => {
    setIsLoading(true);
    setError('');
    const result = await fetchPrintersFromBartender(authToken);
    setIsLoading(false);

    if (result.success) {
      setPrinters(result.data.data);
    } else {
      setError(result.error || 'Failed to load printers.');
      setPrinters([]); // Clear printers on error
    }
  }, [authToken]);

  // Separate effect for restoring saved printer - only runs when printers change and we haven't restored yet
  useEffect(() => {
    if (printers.length > 0 && !selectedPrinter && !hasRestoredFromStorage) {
      const savedPrinterId = localStorage.getItem('mixo-printer-last-selected');
      if (savedPrinterId) {
        // Check if the saved printer exists in the current printer list
        const savedPrinterExists = printers.some(printer => printer.PrinterId === savedPrinterId);
        if (savedPrinterExists) {
          // Create a synthetic event to restore the saved printer
          const syntheticEvent = {
            target: { value: savedPrinterId }
          };
          onPrinterChange(syntheticEvent);
        }
      }
      setHasRestoredFromStorage(true);
    }
  }, [printers, selectedPrinter, hasRestoredFromStorage, onPrinterChange]);

  useEffect(() => {
    loadPrinters();
  }, [loadPrinters]);

  const filteredPrinters = printers.filter((printer) =>
    printer.Name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    printer.Client.FQDName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group printers by FQDName
  const groupedPrinters = filteredPrinters.reduce((groups, printer) => {
    const fqdName = printer.Client.FQDName;
    if (!groups[fqdName]) {
      groups[fqdName] = [];
    }
    groups[fqdName].push(printer);
    return groups;
  }, {});

  // Sort groups by FQDName and printers within each group by name
  const sortedGroupKeys = Object.keys(groupedPrinters).sort();
  sortedGroupKeys.forEach(key => {
    groupedPrinters[key].sort((a, b) => a.Name.localeCompare(b.Name));
  });

  return (
    <div className="form-group">
      <label htmlFor="printer-select">Printer:</label>
      <input
        type="text"
        id="printer-search"
        className="form-control mb-2"
        placeholder="Search for a printer..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        disabled={isLoading || !!error}
      />
      <select
        id="printer-select"
        value={selectedPrinter}
        onChange={onPrinterChange}
        className="form-control"
        disabled={isLoading || !!error}
      >
        <option value="">{isLoading ? 'Loading printers...' : error ? 'Error loading printers' : '-- Select Printer --'}</option>
        {!isLoading && !error && sortedGroupKeys.map((fqdName) => (
          <optgroup key={fqdName} label={fqdName}>
            {groupedPrinters[fqdName].map((printer) => (
              <option key={printer.PrinterId} value={printer.PrinterId}>
                {printer.Name} ({printer.Client.FQDName})
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      {error && <p className="text-danger">{error}</p>}
      {error && <button onClick={loadPrinters} className="btn btn-sm btn-secondary mt-2">Retry Load</button>}
    </div>
  );
};

export default PrinterSelect;

